import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, ClockCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useIndustry } from "@/context/IndustryContext";
import { useAuth } from "@/contexts/AuthContext";

const RECENT_KEY = "fr_recent_searches";
const MAX_RECENT = 5;

// Industry-specific search hints so a pest-control tenant doesn't see
// "landlords / portfolios" (PM concepts) in the search placeholder.
const PLACEHOLDER = {
  pm:            "Search FR + ClickUp… WOs, landlords, chats, comments",
  hvac:          "Search FR + ClickUp… clients, service calls, chats",
  roofing:       "Search FR + ClickUp… homeowners, jobs, chats",
  pest_control:  "Search FR + ClickUp… accounts, tickets, chats",
  home_health:   "Search FR + ClickUp… patients, visits, chats",
  plumbing:      "Search FR + ClickUp… customers, service calls, chats",
  electrical:    "Search FR + ClickUp… customers, service calls, chats",
  handyman:      "Search FR + ClickUp… customers, jobs, chats",
  vendors:       "Search FR + ClickUp… property owners, WOs, chats",
};

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").slice(0, MAX_RECENT); }
  catch { return []; }
}
function saveRecent(q) {
  if (!q || q.length < 2) return;
  try {
    const prev = loadRecent().filter(x => x.toLowerCase() !== q.toLowerCase());
    const next = [q, ...prev].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

/**
 * GlobalSearchBar — searches across landlords, portfolios, jobs, projects,
 * inspections, units, and assets. Debounced. Keyboard: Esc closes.
 * Remembers the last 5 searches per browser (localStorage) and shows them
 * when the bar opens empty.
 */
export default function GlobalSearchBar({ dark = false }) {
  const { industry } = useIndustry();
  const { user } = useAuth();
  const chatPath = user?.role === "worker" ? "/worker/team-chat" : "/admin/team-chat";
  const placeholder = PLACEHOLDER[industry] || PLACEHOLDER.pm;
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [cuResults, setCuResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(loadRecent());
  const boxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); setCuResults(null); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      // Fan out to BOTH the FR search endpoint and the ClickUp chat/comment
      // search in parallel. If the caller has no CU personal token bound,
      // the CU call throws — swallow it silently so FR results still show.
      try {
        const [frRes, cuRes] = await Promise.allSettled([
          api.get(`/search?q=${encodeURIComponent(q.trim())}`),
          api.get(`/clickup/chat/search?q=${encodeURIComponent(q.trim())}&limit=8`),
        ]);
        setResults(frRes.status === "fulfilled" ? frRes.value.data.results : null);
        setCuResults(cuRes.status === "fulfilled" ? cuRes.value.data : null);
        setOpen(true);
      } catch (e) { setResults(null); setCuResults(null); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (path) => {
    saveRecent(q.trim());
    setRecent(loadRecent());
    setOpen(false); setQ(""); setResults(null); setCuResults(null);
    nav(path);
  };
  const runRecent = (term) => { setQ(term); setOpen(true); };
  const clearRecent = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecent([]);
  };

  const frHits = results ? Object.values(results).reduce((a, arr) => a + arr.length, 0) : 0;
  const cuChanHits = cuResults?.channels?.length || 0;
  const cuCommentHits = cuResults?.comments?.length || 0;
  const totalHits = frHits + cuChanHits + cuCommentHits;
  const categories = [
    { key: "portfolios", label: "Portfolios", color: "bg-cyan-600" },
    { key: "units",      label: "Units",      color: "bg-fuchsia-500" },
    { key: "landlords",  label: "Landlords",  color: "bg-fuchsia-600" },
    { key: "jobs",       label: "Jobs",       color: "bg-emerald-600" },
    { key: "projects",   label: "Projects",   color: "bg-amber-600" },
    { key: "inspections",label: "Inspections",color: "bg-blue-600" },
    { key: "assets",     label: "Assets",     color: "bg-purple-600" },
  ];

  const showRecent = open && !q.trim() && recent.length > 0;

  return (
    <div ref={boxRef} className="relative w-full" data-testid="global-search-wrap">
      <MagnifyingGlass weight="bold" size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${dark ? "text-white/70" : "text-slate-500"}`}/>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={e => e.key === "Escape" && setOpen(false)}
        placeholder={placeholder}
        data-testid="global-search-input"
        className={`w-full h-10 pl-9 pr-9 border-2 font-medium text-sm ${dark ? "bg-white/10 border-white/30 text-white placeholder-white/60" : "bg-white border-[#0f172a] text-slate-900"}`}
      />
      {q && (
        <button onClick={() => { setQ(""); setResults(null); setCuResults(null); setOpen(false); }}
          data-testid="global-search-clear"
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 ${dark ? "text-white/70 hover:text-white" : "text-slate-500 hover:text-slate-800"}`}>
          <X size={14}/>
        </button>
      )}

      {showRecent && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-[#0f172a] shadow-2xl z-50" data-testid="global-search-recent">
          <div className="px-3 py-1 bg-slate-100 flex items-center gap-2">
            <ClockCounterClockwise weight="bold" size={11} className="text-slate-500"/>
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">Recent Searches</span>
            <button onClick={clearRecent} data-testid="global-search-recent-clear"
              className="ml-auto text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-red-600">Clear</button>
          </div>
          {recent.map((term, i) => (
            <button key={i} onClick={() => runRecent(term)}
              data-testid={`global-search-recent-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100 flex items-center gap-2">
              <MagnifyingGlass size={11} className="text-slate-400"/>
              <span className="text-sm font-bold text-slate-900 truncate">{term}</span>
            </button>
          ))}
        </div>
      )}

      {open && (results || cuResults) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-[#0f172a] shadow-2xl max-h-96 overflow-y-auto z-50" data-testid="global-search-results">
          {busy && <div className="p-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Searching…</div>}
          {!busy && totalHits === 0 && (
            <div className="p-4 text-sm text-slate-500 font-medium" data-testid="global-search-empty">
              No matches for "<b>{q}</b>". Try a shorter query or different words.
              <button onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("proman:ask", { detail: { message: q.trim() } })); }}
                data-testid="global-search-ask-radar"
                className="mt-2 w-full h-9 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest hover:bg-[#00C4CC]">
                Ask Radar instead →
              </button>
            </div>
          )}
          {!busy && totalHits > 0 && categories.map(cat => {
            const rows = (results || {})[cat.key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={cat.key}>
                <div className={`sticky top-0 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-white ${cat.color}`}>
                  {cat.label} ({rows.length})
                </div>
                {rows.map(r => (
                  <button key={`${cat.key}-${r.id}`} onClick={() => go(r.path)}
                    data-testid={`global-search-hit-${cat.key}-${r.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold text-slate-900 truncate">{r.label || "(untitled)"}</div>
                      {typeof r.unit_count === "number" && r.unit_count > 0 && (
                        <span data-testid={`global-search-units-badge-${r.id}`}
                          className="shrink-0 inline-flex items-center px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 border border-cyan-300">
                          {r.unit_count} {r.unit_count === 1 ? "unit" : "units"}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{r.sub}</div>
                  </button>
                ))}
              </div>
            );
          })}
          {!busy && cuChanHits > 0 && (
            <div>
              <div className="sticky top-0 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-white bg-[#7B61FF]">
                ClickUp Chats ({cuChanHits})
              </div>
              {cuResults.channels.map(ch => (
                <button key={`cuchan-${ch.id}`} onClick={() => {
                  const params = new URLSearchParams({
                    cu: ch.id,
                    cuName: ch.name || "",
                    cuType: ch.type || "CHANNEL",
                  });
                  go(`${chatPath}?${params.toString()}`);
                }}
                  data-testid={`global-search-hit-cuchan-${ch.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#7B61FF] text-white">
                      {(ch.type || "CH").slice(0, 5)}
                    </span>
                    <div className="text-sm font-bold text-slate-900 truncate">{ch.name || "(unnamed)"}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {(ch.counterparts || []).map(c => c.name).filter(Boolean).join(" · ") || "Open in Team Chat"}
                  </div>
                </button>
              ))}
            </div>
          )}
          {!busy && cuCommentHits > 0 && (
            <div>
              <div className="sticky top-0 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-white bg-[#4A2FCC]">
                ClickUp Comments ({cuCommentHits})
              </div>
              {cuResults.comments.map(cm => (
                <button key={`cucm-${cm.id}`} onClick={() => go(`${chatPath}?channel=my_clickup`)}
                  data-testid={`global-search-hit-cucm-${cm.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold text-slate-900 truncate">
                      {cm.author || "ClickUp"}{cm.wo_number ? ` · WO ${cm.wo_number}` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{cm.preview}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
